<template>
  <div class="wrapper">
    <NuxtLayout :is-error-layout="true">
      <Grid variant="default">
        <div class="error-page">
          <HeroSimple
            :title="pageData.title"
            :description="pageData.description"
            :bottom-padding="true"
            class="error-page-header"
          />

          <form action="" class="search-form" @submit.prevent="goToQuery">
            <div class="search-wrapper">
              <div class="search-form-input-wrapper">
                <input
                  id="search"
                  ref="searchInput"
                  v-model="q"
                  type="text"
                  placeholder="Vul hier je zoekterm in..."
                  class="search-form-text-input"
                  autocomplete="off"
                  @keydown.enter="goToQuery"
                />

                <button
                  v-if="q && q.length > 0"
                  class="search-form-clear"
                  aria-label="Leeg het zoekveld"
                  @click.prevent="handleClear"
                >
                  <Icon
                    name="eenvandaag-general:x-close"
                    class="search-form-clear-icon"
                    role="presentation"
                  />
                </button>

                <label v-if="!q" for="search">
                  <Icon
                    name="eenvandaag-general:search"
                    class="search-form-search-icon"
                    role="presentation"
                  />
                </label>
              </div>
            </div>
          </form>

          <SectionMostRead title="Meest gelezen" :is-error-layout="false" />
        </div>
      </Grid>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import usePageStore from '~/stores/page';

import type { IError } from '~~/types/local_types';
const q = ref('');
const { stripHTML } = formatUtils();

const props = defineProps({
  error: {
    type: Object as () => IError,
    default: () => {},
  },
});

// setup the page store
usePageStore();
const router = useRouter();

const isServerError = (props.error?.statusCode ?? 500) >= 500;

const pageData = isServerError
  ? {
      title: 'Er ging iets mis...',
      description: `Oeps! Deze pagina kon even niet worden geladen. Probeer het later nog eens, ga terug naar de <a href='/'>homepage</a> of gebruik onze zoekfunctie om te vinden wat je nodig hebt.`,
      error: props.error,
    }
  : {
      title: 'Pagina niet gevonden...',
      description: `Oeps! De pagina die je zoekt bestaat niet (meer) of is verplaatst. Geen zorgen, je kunt terug naar de <a href='/'>homepage</a> of onze zoekfunctie gebruiken om te vinden wat je nodig hebt.`,
      error: props.error,
    };

// Also needed on error page
useDefaultSchema();
/**
 * Set SEO meta tags
 */
usePageSeo({
  pageTitle: pageData.title,
  pageDescription: stripHTML(pageData.description),
});

if (import.meta.client) {
  // e.g. `404:error` / `500:error`, so 404s, bots and real failures can be told apart in NPO Tag
  sendAnalytics({ prefix: 'error', page: String(props.error?.statusCode ?? 'unknown') });
}

onBeforeRouteLeave(() => {
  clearError();
});

const goToQuery = () => {
  const term = q.value.trim();
  if (!term) return;
  router.push({ path: '/zoeken', query: { q: term } });
};

const handleClear = () => {
  // Setting it empty if the user clicks on the clear button
  q.value = '';
};
</script>

<style scoped lang="postcss">
.wrapper {
  background-color: hsl(var(--color-neutral-white));
}

.error-page {
  --error-page-gap: var(--unit-16);
  display: flex;
  flex-direction: column;
  gap: var(--error-page-gap);
  justify-content: space-between;
  height: 100%;

  @media (--lg-viewport) {
    --error-page-gap: var(--unit-32);
  }
}

.error-page-header {
  --page-header-content-padding-bottom: var(--unit-24);

  padding-bottom: var(--page-header-content-padding-bottom);
  @media (--lg-viewport) {
  }
}

.search-form {
  --search-form-padding-bottom: var(--unit-24);
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  border-bottom: 2px solid hsl(var(--color-primary-100));
  padding-bottom: var(--search-form-padding-bottom);

  @media (--md-viewport) {
    --search-form-padding-bottom: var(--unit-40);
  }
}

.search-wrapper {
  display: flex;
  position: relative;
  align-items: stretch;
  width: 100%;

  @media (--md-viewport) {
    width: 50%;
  }
}

.search-form-input-wrapper {
  --search-input-wrapper-width: 100%;
  --search-header-search-input-height: var(--unit-42);

  width: var(--search-input-wrapper-width);
  height: var(--search-header-search-input-height);
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;

  @media (--lg-viewport) {
    --search-header-search-input-height: 53px;
  }
}

.search-form-text-input {
  --search-header-text-input-padding: 0 0 0 var(--unit-16);
  --search-header-text-input-height: var(--unit-42);
  --search-header-text-input-border: 1px solid hsl(var(--color-primary-100));
  --search-header-text-input-border-focus: 1px solid #cdcdda;
  --search-header-search-focus-input-color: hsl(var(--color-primary-500));
  --search-header-search-input-color: hsl(var(--color-primary-700));

  padding: var(--search-header-text-input-padding);
  height: var(--search-header-text-input-height);
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  color: var(--search-header-search-input-color);
  border: var(--search-header-text-input-border);
  background: var(--effect-gradient-btn-secondary);
  border-radius: var(--unit-4);
  transition: padding 250ms ease;
  text-overflow: ellipsis;
  box-shadow:
    0 -1px 0 0 rgba(16, 24, 40, 0.06) inset,
    0 4px 15px 0 #dfe0ec inset;

  @add-mixin body-md-semi-bold;

  &::placeholder {
    color: var(--search-header-search-focus-input-color);
    @add-mixin body-md;
  }

  &:focus {
    outline: none;
    outline-offset: 0;
    border: var(--search-header-text-input-border-focus);
  }

  @media (--lg-viewport) {
    --search-header-text-input-padding: 0 0 0 var(--unit-16);
    --search-header-text-input-height: 53px;
  }
}

.search-form-search-icon {
  --search-header-search-icon-size: var(--unit-18);
  --search-header-search-icon-color: hsl(var(--color-primary-700));

  position: absolute;
  top: 50%;
  translate: 0 -50%;
  color: var(--search-header-search-icon-color);
  z-index: 1;
  right: var(--unit-16);
  font-size: var(--search-header-search-icon-size);

  @media (--lg-viewport) {
    --search-header-search-icon-size: var(--unit-22);
  }
}

.search-form-clear {
  position: absolute;
  top: 50%;
  translate: 0 -50%;
  cursor: pointer;
  display: flex;
  padding: 0;
  z-index: 1;
  right: var(--unit-16);
  outline: none;
  border: none;
  border-radius: var(--unit-2);
  background: none;

  &:focus-visible {
    outline: 2px solid hsl(var(--color-primary-700));
  }
}

.search-form-clear-icon {
  --search-header-clear-icon-size: var(--unit-18);
  --search-header-clear-icon-color: hsl(var(--color-primary-500));

  color: var(--search-header-clear-icon-color);
  font-size: var(--search-header-clear-icon-size);

  @media (--lg-viewport) {
    --search-header-clear-icon-size: var(--unit-22);
  }
}
</style>
